// @ts-strict-ignore
import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import { flatMap } from 'lodash';
import * as React from 'react';
import {
    IntegrationAppCenterBeta,
    IntegrationAzureIT,
    IntegrationFirebaseBeta,
    IntegrationInstallationId,
    IntegrationLaunchDarkly,
    IntegrationOptimizely,
    IntegrationProviderType,
    IntegrationTestFlight,
    IntegrationType,
    JiraServiceManagementIntegrationData,
    OpsgenieIntegrationData,
} from '../types';
import { integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { RootState } from '../reducers/reducers';
import {
    selectAppSettings,
    selectAppStoreType,
    selectOrgProviders,
    selectAppName,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import { useParams } from 'react-router-dom';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { oauthInstallationUrlForIntegrationType, validateUpdateIntegrationValues } from '../helpers/integrationHelpers';
import Link from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';
import { updateIntegrationFormDataForIntegration } from '../helpers/integrationFormHelpers';
import { UpdateIntegrationModalData } from './setup/useUpdateIntegrationModal';
import { globalMessage } from '../utils/globalMessage';
import { useEffect } from 'react';

interface IntegrationConfigurationProps extends UpdateIntegrationModalData {
    onClose: () => void;
}

const IntegrationConfiguration: React.FC<IntegrationConfigurationProps> = (props: IntegrationConfigurationProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);

    const [selectedTestflightBundleId, setSelectedTestflightBundleId] = React.useState(
        (props.integration as IntegrationTestFlight)?.data?.bundleId ?? null,
    );
    const [selectedAppCenterAppId, setSelectedAppCenterAppId] = React.useState(
        (props.integration as IntegrationAppCenterBeta)?.data?.selectedApp?.id ?? null,
    );
    const [selectedAzureProjectId, setSelectedAzureProjectId] = React.useState(
        (props.integration as IntegrationAzureIT)?.data.selectedProject?.id ?? null,
    );
    const [selectedOptimizelyProjectId, setSelectedOptimizelyProjectId] = React.useState(
        (props.integration as IntegrationOptimizely)?.data.selectedProject?.id ?? null,
    );

    const [selectedLaunchDarklyProjectId, setSelectedLaunchDarklyProjectId] = React.useState(
        (props.integration as IntegrationLaunchDarkly)?.data.selectedProject?.id ?? null,
    );

    const [selectedFirebaseProjectId, setSelectedFirebaseProjectId] = React.useState(
        (props.integration as IntegrationFirebaseBeta)?.data.projectId ?? null,
    );

    const [selectedOpsgenieSchedule, setOpsgenieSelectedSchedule] = React.useState<string>(
        (props.integration?.data as OpsgenieIntegrationData)?.selectedSchedule?.id ?? null,
    );

    const [selectedJSMSchedule, setJSMSelectedSchedule] = React.useState<string>(
        (props.integration?.data as JiraServiceManagementIntegrationData)?.selectedSchedule?.id ?? null,
    );

    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration?.providerId);

        return provider ?? null;
    });

    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, props.appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;
        setFormIsValid(isValid);
    };

    const onSubmit = (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                const requestData = validateUpdateIntegrationValues(integrationType, integrationProvider, values);

                if (requestData) {
                    props.updateIntegration(integrationInstallationId, integrationType, requestData);
                } else {
                    throw Error('Invalid form values');
                }
            })
            .catch((e) => {
                globalMessage.error(e.message);
                setFormIsValid(false);
            });
    };

    const onClose = () => {
        form.resetFields();

        props.onClose();
    };

    const onChangeBundleId = (value) => {
        form.resetFields(['selectedBetaGroupIds']);
        setSelectedTestflightBundleId(value);
    };

    const onChangeAppCenterBetaAppId = (value) => {
        setSelectedAppCenterAppId(value);
        form.resetFields(['selectedBetaGroupIds']);
    };

    const onChangeAzureProjectId = (value) => {
        form.resetFields(['projects']);
        setSelectedAzureProjectId(value);
    };

    const onChangeOptimizelyProjectId = (value) => {
        form.resetFields(['environmentId']);
        setSelectedOptimizelyProjectId(value);
    };

    const onChangeLaunchDarklyProjectId = (value) => {
        form.resetFields(['environmentId']);
        setSelectedLaunchDarklyProjectId(value);
    };

    const onChangeFirebaseProjectId = (value) => {
        form.resetFields(['firebaseAppId', 'selectedBetaGroupIds']);
        setSelectedFirebaseProjectId(value);
    };

    const onChangeOpsgenieSchedule = (value) => {
        form.resetFields(['rotationId']);
        setOpsgenieSelectedSchedule(value);
    };

    const onChangeJSMSchedule = (value) => {
        form.resetFields(['rotationId']);
        setJSMSelectedSchedule(value);
    };

    const [form] = useForm();

    const selectedAutoSelectAllJiraProjects = useWatch(['autoSelectAllProjects'], form);
    useEffect(() => {
        form.resetFields(['projects']);
    }, [selectedAutoSelectAllJiraProjects]);

    const selectedBitriseIsPipeline = useWatch(['bitriseIsPipeline'], form);
    const selectedBitriseIsPipelineDev = useWatch(['bitriseIsPipelineDev'], form);
    const selectedBitriseIsPipelineProd = useWatch(['bitriseIsPipelineProd'], form);

    const fields = updateIntegrationFormDataForIntegration(
        props.integration,
        appPlatform,
        appStoreType,
        props.appId,
        appName,
        workflowEnabledSteps,
        integrationProvider,
        onChangeBundleId,
        selectedTestflightBundleId,
        onChangeAppCenterBetaAppId,
        selectedAppCenterAppId,
        onChangeAzureProjectId,
        selectedAzureProjectId,
        onChangeOptimizelyProjectId,
        selectedOptimizelyProjectId,
        onChangeLaunchDarklyProjectId,
        selectedLaunchDarklyProjectId,
        onChangeFirebaseProjectId,
        selectedFirebaseProjectId,
        onChangeOpsgenieSchedule,
        selectedOpsgenieSchedule,
        onChangeJSMSchedule,
        selectedJSMSchedule,
        selectedAutoSelectAllJiraProjects,
        selectedBitriseIsPipeline,
        selectedBitriseIsPipelineDev,
        selectedBitriseIsPipelineProd,
    );
    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onSubmit,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
            )
        :   null;

    return (
        <Modal
            title={`Complete ${props.integration.name} setup`}
            open={props.open}
            confirmLoading={props.isLoading}
            maskClosable={props.closable}
            closable={props.closable}
            onCancel={onClose}
            footer={
                <Button onClick={() => form.submit()} disabled={!formIsValid} loading={props.isLoading} type="primary">
                    Submit
                </Button>
            }
            centered
            destroyOnClose
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
        >
            <div>
                {modalContent}
                {additionalInfoForProvider && <div style={{ marginTop: 12 }}>{additionalInfoForProvider}</div>}
            </div>
        </Modal>
    );
};

export default IntegrationConfiguration;

export const additionalInfoNodeForProvider = (
    providerType: IntegrationProviderType,
    integrationType: IntegrationType,
    orgId: string,
    orgIdParam: string,
    appIdParam: string,
    integrationInstallationId?: IntegrationInstallationId,
) => {
    if (providerType === IntegrationProviderType.GitHub) {
        const installURL = oauthInstallationUrlForIntegrationType(
            integrationType,
            orgId,
            orgIdParam,
            appIdParam,
            undefined,
            integrationInstallationId,
        );
        return (
            <Text>
                Don&apos;t see your repo? <Link href={installURL}>Authorize more repos.</Link>
            </Text>
        );
    }

    return null;
};
